import { useState } from 'react';

import { Accordion, Radio } from '@iwoca/orion';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';

import styles from './BankStatementsDE.module.css';
import ManualUploadDE from './ManualUploadDE';
import { useSplitTest } from '../../../../../hooks/useSplitTest';
import { useGetContext } from '../../../../../lendingApi/lendingApiQueries';
import {
  TOfferDataRequirement,
  TTrueLayerAlertStatus,
} from '../../../../../types';
import Required from '../../../DocumentRequirement/Required';
import TrueLayer from '../../../TrueLayer/TrueLayer';
import { getLinkedBankAccountDetails } from '../../../TrueLayer/trueLayer.helpers';
import TrueLayerAlertBox from '../../../TrueLayer/TrueLayerAlertBox/TrueLayerAlertBox';
import TrueLayerLinkedAccount from '../../../TrueLayer/TrueLayerLinkedAccount/TrueLayerLinkedAccount';

type TProps = {
  quantifier: number;
  status?: TOfferDataRequirement['actions'][number]['status'];
  isSubmitted: boolean;
  resolutionMethods: TOfferDataRequirement['actions'][number]['resolution_methods'];
};

type TRequirementType = 'manual' | 'bank_account' | 'de_bank_and_manual_upload';

const BankStatementsDE = ({
  quantifier,
  status,
  isSubmitted,
  resolutionMethods,
}: TProps) => {
  const { hasDeBSRequiredEnabled, hasDeAnyBSRequiredEnabled } = useSplitTest();

  const [requirementType, setRequirementType] = useState<TRequirementType>(
    hasDeBSRequiredEnabled ? 'de_bank_and_manual_upload' : 'bank_account',
  );

  const [trueLayerAlertStatus, setTrueLayerAlertStatus] =
    useState<TTrueLayerAlertStatus>(null);

  const { context } = useGetContext();

  const { t } = useLingui();

  const REQUIREMENTS = [
    { header: t`Link my bank account`, value: 'bank_account' },
    { header: t`Add business bank statements`, value: 'manual' },
  ] as const;

  const renderRequirements = () => {
    switch (requirementType) {
      case 'bank_account':
        return <TrueLayer setAlertStatus={setTrueLayerAlertStatus} />;
      case 'manual':
        return (
          <ManualUploadDE
            title={title}
            status={status}
            isSubmitted={isSubmitted}
          />
        );
      case 'de_bank_and_manual_upload':
        //Show both options in DE test.
        return (
          <>
            <TrueLayer setAlertStatus={setTrueLayerAlertStatus} />
            <Accordion
              title={t`My bank is not available to link`}
              small
              variant="secondary"
            >
              <ManualUploadDE
                title={title}
                status={status}
                isSubmitted={isSubmitted}
              />
            </Accordion>
          </>
        );
    }
  };

  const title = plural(quantifier, {
    one: 'Business bank statements for the last # month',
    other: 'Business bank statements for the last # months',
  });

  const showSubtitle = !hasDeAnyBSRequiredEnabled && !hasDeBSRequiredEnabled;

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        {/* id has space instead of hyphen to match other dynamic ids used for scrolling to requirements */}
        <h4
          className={clsx(
            styles.title,
            showSubtitle && styles.titleWithSubtitle,
          )}
          id="document-requirement-bank statement"
        >
          {title}
          {isSubmitted && (status === 'required' || status === 'error') && (
            <Required />
          )}
        </h4>
        {showSubtitle && (
          <h5 className={styles.subTitle}>
            <Trans>How would you like to share these?</Trans>
          </h5>
        )}
      </div>

      {!hasDeBSRequiredEnabled &&
        REQUIREMENTS.map((requirement) => (
          <div className={styles.radioSection} key={requirement.value}>
            <Radio
              label={requirement.header}
              onChange={() => setRequirementType(requirement.value)}
              checked={requirementType === requirement.value}
              frame
            />
          </div>
        ))}
      {renderRequirements()}
      {trueLayerAlertStatus && (
        <TrueLayerAlertBox status={trueLayerAlertStatus} />
      )}
      <TrueLayerLinkedAccount
        linkedBankAccount={getLinkedBankAccountDetails(
          resolutionMethods,
          context,
        )}
      />
    </div>
  );
};

export default BankStatementsDE;
